import React from 'react';
import { cn } from '../../utils/classSupport';
import { MoreHorizontal, ArrowUpDown, CheckCircle, Clock, AlertCircle, Circle } from 'lucide-react';
import { changeDateToString, getDateFullFormat } from '../../utils/Date/SupportDate';
import { coverNameToID } from '../../utils/Branch/SupportBranch'
import { useSelector } from 'react-redux';

const statusColors = {
    'Submitted': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'Recorded': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    'Processing': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    'Completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
};

const priorityColors = {
    'High': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border-red-200 dark:border-red-800',
    'Medium': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 border-orange-200 dark:border-orange-800',
    'Low': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 border-blue-200 dark:border-blue-800',
};

const statusIcons = {
    'Submitted': AlertCircle,
    'Recorded': Circle,
    'Processing': Clock,
    'Completed': CheckCircle,
};

const typeColors = {
    'Database': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'Report': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    'Mail': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
    'Web': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    'Application': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    'Idea': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    'Other': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
};

export default function FeddbackErrorContainer({ bugs, onStatusChange, onEdit, onDelete, onSaveAddActivity }) {
    const [expandedRows, setExpandedRows] = React.useState({});
    const [hoverRow, setHoverRow] = React.useState(null);
    const [editingCell, setEditingCell] = React.useState(null);
    const [editValue, setEditValue] = React.useState('');
    const [showActivitiesModal, setShowActivitiesModal] = React.useState(null);
    const [newActivityDescription, setNewActivityDescription] = React.useState('');
    const [newActivityPerson, setNewActivityPerson] = React.useState('');
    const [showSystemActivities, setShowSystemActivities] = React.useState(false);
    const [showPersonDropdown, setShowPersonDropdown] = React.useState(false);
    const [personSearchInput, setPersonSearchInput] = React.useState('');
    const auth = useSelector(state => state.auth);
    const { role } = auth.user;

    const isAdmin = role === 7;
    
    // Get unique people list from bugs
    const uniquePeople = React.useMemo(() => {
        // const people = new Set(bugs.map(bug => bug.personHandling).filter(Boolean));
        // return Array.from(people).sort();
        if(role === 7) return ["Hai", "Lam"]
        else return []
        
    }, [bugs]);

    // Filter people based on search input
    const filteredPeople = React.useMemo(() => {
        if (!personSearchInput.trim()) return uniquePeople;
        return uniquePeople.filter(person => 
            person.toLowerCase().includes(personSearchInput.toLowerCase())
        );
    }, [uniquePeople, personSearchInput]);

    const toggleExpand = (bugId) => {
        setExpandedRows(prev => ({
            ...prev,
            [bugId]: !prev[bugId]
        }));
    };

    const startEdit = (bugId, field, value) => {
        setEditingCell({ bugId, field });
        setEditValue(value);
    };

    const saveEdit = (bugId, field) => {
        if (editValue.trim() !== '') {
            onEdit(bugId,  field ,{ [field]: editValue });
           // handleEditBug = (bugId, target, updates) 
        }
        setEditingCell(null);
        setEditValue('');
    };

    const cancelEdit = () => {
        setEditingCell(null);
        setEditValue('');
    };

    const handleAddActivity = () => {
        if (newActivityDescription.trim() && newActivityPerson.trim()) {
            const newActivity = {
                date: new Date(),
                description: newActivityDescription,
                person: newActivityPerson
            };
            const updatedBug = bugs.find(b => b._id === showActivitiesModal);
            const updatedActivities = [...(updatedBug?.activities || []), newActivity];
            onEdit(showActivitiesModal, 'activities', newActivity);
            setNewActivityDescription('');
            setNewActivityPerson('');
            setPersonSearchInput('');
        }
    };

    return (
        <div className="w-full overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-muted-foreground uppercase text-xs font-medium">
                        <tr>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7", width: "70px"}}>Time</th>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7", width: "60px"}}>Branch</th>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7", width: "60px"}}>Project</th>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7", width: "450px"}}>Content</th>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7", width: "50px"}}>Image</th>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7", display: "none"}}>Person</th>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7", width: "85px"}}>Deadline</th>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7", width: "85px"}}>Status</th>
                            <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7"}}>Activities</th>
                            {/* <th style={{fontSize:"1.2rem", textAlign:"center", background:"#c7c7c7"}}>Actions</th> */}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {bugs.map((bug) => {
                            // console.log("bug", bug._id);
                           // const StatusIcon = statusIcons[bug.status] || Circle;
                            return (
                                <tr key={bug._id} className="hover:bg-muted/50 transition-colors">
                                    <td className="px-1 py-3 font-medium" style={{fontSize: "1.1rem"}}>
                                        <div>{(bug.createdAt.slice(0,10))}<br/>@{(bug.createdAt.slice(11,16))}</div>
                                    </td>
                                    <td className="px-1 py-2 font-medium" style={{fontSize: "1.2rem"}}>AZ{coverNameToID(bug.branchID)}</td>
                                    <td style={{fontSize: "1.2rem"}}>
                                        <div className="relative group">
                                            <select
                                                value={bug.project || 'Other'}
                                                style={{fontSize:"1.2rem"}} 
                                                onChange={(e) => onEdit(bug._id, 'project', { project: e.target.value })}
                                                className={cn(
                                                    "appearance-none pl-8 pr-8 py-1 rounded-full text-xs font-semibold cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring border-none",
                                                    typeColors[bug.project] || typeColors['Other']
                                                )}
                                            >
                                                <option style={{fontSize:"1.2rem"}} value="Database">Database</option>
                                                <option style={{fontSize:"1.2rem"}} value="Report">Report</option>
                                                <option style={{fontSize:"1.2rem"}} value="Mail">Mail</option>
                                                <option style={{fontSize:"1.2rem"}} value="Web">Web</option>
                                                <option style={{fontSize:"1.2rem"}} value="Application">Application</option>
                                                <option style={{fontSize:"1.2rem"}} value="Idea">Idea</option>
                                                <option style={{fontSize:"1.2rem"}} value="Other">Other</option>
                                            </select>
                                        </div>
                                    </td>
                                    <td  style={{fontSize: "1.2rem"}}>
                                        <div 
                                            style={{display: 'flex', gap: '8px', alignItems: 'flex-start'}}
                                            onMouseEnter={() => setHoverRow(bug._id)}
                                            onMouseLeave={() => setHoverRow(null)}
                                        >
                                            <div  style={{flex: 1, minWidth: 0}} >
                                                {editingCell?.bugId === bug._id && editingCell?.field === 'content' && !isAdmin ? (
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                                        <textarea
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            style={{
                                                                width: "100%",
                                                                minHeight: "80px",
                                                                padding: "8px",
                                                                borderRadius: "4px",
                                                                border: "2px solid #3498db",
                                                                fontFamily: "inherit",
                                                                fontSize: "inherit",
                                                                resize: "vertical"
                                                            }}
                                                            autoFocus
                                                        />
                                                        <div style={{display: 'flex', gap: '6px'}}>
                                                            <button
                                                                onClick={() => saveEdit(bug._id, 'content')}
                                                                style={{
                                                                    padding: '4px 10px',
                                                                    fontSize: '12px',
                                                                    backgroundColor: '#27ae60',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '3px',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                style={{
                                                                    padding: '4px 10px',
                                                                    fontSize: '12px',
                                                                    backgroundColor: '#e74c3c',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '3px',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : expandedRows[bug._id] ? (
                                                    <textarea
                                                        value={bug.content}
                                                        readOnly
                                                        onDoubleClick={() => startEdit(bug._id, 'content', bug.content)}
                                                        style={{
                                                            width: "100%",
                                                            minHeight: "80px",
                                                            padding: "8px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #ccc",
                                                            fontFamily: "inherit",
                                                            fontSize: "inherit",
                                                            resize: "vertical",
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                ) : (
                                                    <div 
                                                        className="truncate" 
                                                        title={bug.content}
                                                        style={{
                                                            padding: '4px 8px',
                                                            backgroundColor: hoverRow === bug._id ? '#f0f8ff' : 'transparent',
                                                            borderRadius: '4px',
                                                            transition: 'background-color 0.2s',
                                                            cursor: 'pointer',
                                                            maxHeight: '60px',
                                                            overflow: 'hidden',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            wordBreak: 'break-word',
                                                        }}
                                                        onDoubleClick={() => startEdit(bug._id, 'content', bug.content)}
                                                        onClick={() => toggleExpand(bug._id)}
                                                    >
                                                        {bug.content}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{fontSize: "1.2rem"}}>
                                        {bug.image && (
                                            <div style={{ display: 'inline-block' }}>
                                                <a 
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setHoverRow(`image-${bug._id}`);
                                                    }}
                                                    className="text-primary hover:underline"
                                                    onMouseEnter={(e) => setHoverRow(`image-${bug._id}`)}
                                                    onMouseLeave={(e) => setHoverRow(null)}
                                                >
                                                    View
                                                </a>
                                                {hoverRow === `image-${bug._id}` && (
                                                    <>
                                                        <div 
                                                            style={{
                                                                position: 'fixed',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                                                zIndex: 9998,
                                                                cursor: 'auto'
                                                            }}
                                                            onMouseLeave={() => setHoverRow(null)}
                                                        />
                                                        <div 
                                                            style={{
                                                                position: 'fixed',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                zIndex: 9999,
                                                                backgroundColor: 'white',
                                                                border: '2px solid #3498db',
                                                                borderRadius: '8px',
                                                                padding: '8px',
                                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                                                maxWidth: '85vw',
                                                                maxHeight: '85vh',
                                                                overflow: 'auto'
                                                            }}
                                                            onMouseEnter={() => setHoverRow(`image-${bug._id}`)}
                                                            onMouseLeave={() => setHoverRow(null)}
                                                        >
                                                            <img 
                                                                src={`/fbTracker/image/${bug._id}`}
                                                                alt="Preview"
                                                                style={{
                                                                    maxWidth: '100%',
                                                                    maxHeight: '100%',
                                                                    borderRadius: '6px',
                                                                    display: 'block'
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{fontSize: "1.2rem"}}>
                                        {editingCell?.bugId === bug._id && editingCell?.field === 'deadline' && isAdmin ? (
                                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                                <input
                                                    type="date"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    style={{
                                                        padding: "8px",
                                                        borderRadius: "4px",
                                                        border: "2px solid #3498db",
                                                        fontFamily: "inherit",
                                                        fontSize: "inherit"
                                                    }}
                                                    autoFocus
                                                />
                                                <div style={{display: 'flex', gap: '6px'}}>
                                                    <button
                                                        onClick={() => saveEdit(bug._id, 'deadline')}
                                                        style={{
                                                            padding: '4px 10px',
                                                            fontSize: '12px',
                                                            backgroundColor: '#27ae60',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '3px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        style={{
                                                            padding: '4px 10px',
                                                            fontSize: '12px',
                                                            backgroundColor: '#e74c3c',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '3px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    padding: '4px 8px',
                                                    backgroundColor: hoverRow === bug._id ? '#f0f8ff' : 'transparent',
                                                    borderRadius: '4px',
                                                    transition: 'background-color 0.2s',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={() => setHoverRow(bug._id)}
                                                onMouseLeave={() => setHoverRow(null)}
                                                onDoubleClick={() => startEdit(bug._id, 'deadline', bug.deadline ? new Date(bug.deadline).toISOString().split('T')[0] : '')}
                                            >
                                                {bug.deadline ? changeDateToString(new Date(bug.deadline)) : ''}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{fontSize: "1.2rem"}}>
                                        <div className="relative group">
                                            <select
                                                value={bug.status}
                                                disabled={!isAdmin}
                                                style={{fontSize:"1.2rem"}} 
                                                onChange={(e) => onStatusChange(bug._id, e.target.value)}
                                                className={cn(
                                                    "appearance-none pl-8 pr-8 py-1 rounded-full text-xs font-semibold cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring border-none",
                                                    statusColors[bug.status] || 'bg-gray-100 text-gray-800'
                                                )}
                                            >
                                                <option style={{fontSize:"1.2rem"}} value="Submitted">Submitted</option>
                                                <option style={{fontSize:"1.2rem"}} value="In Progress">In Progress</option>
                                                <option style={{fontSize:"1.2rem"}} value="Rejected">Rejected</option>
                                                <option style={{fontSize:"1.2rem"}} value="Checking">Checking</option>
                                                <option style={{fontSize:"1.2rem"}} value="Completed">Completed</option>

                                            </select>
                                            {/* <StatusIcon className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-70" /> */}
                                        </div>
                                    </td>
                                    <td  style={{minWidth: "250px", cursor: "pointer", textAlign: "left", fontSize: "1.2rem"}}>
                                        <div 
                                            onClick={() => setShowActivitiesModal(bug._id)}
                                            style={{
                                                padding: '8px',
                                                backgroundColor: bug.activities && bug.activities.length > 0 ? '#f0f8ff' : '#f5f5f5',
                                                borderRadius: '4px',
                                                border: bug.activities && bug.activities.length > 0 ? '1px solid #3498db' : '1px solid #ddd',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = bug.activities && bug.activities.length > 0 ? '#e8f4f8' : '#efefef';
                                                e.currentTarget.style.borderColor = '#3498db';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = bug.activities && bug.activities.length > 0 ? '#f0f8ff' : '#f5f5f5';
                                                e.currentTarget.style.borderColor = bug.activities && bug.activities.length > 0 ? '#3498db' : '#ddd';
                                            }}
                                        >
                                            {bug.activities && bug.activities.length > 0 ? (
                                                <>
                                                    {bug.activities.filter(activity => activity.person.toLowerCase() !== 'system').slice(-2).map((activity, idx) => (
                                                        <div key={idx} style={{marginBottom: idx === 0 ? '4px' : 0, fontSize: '1.1rem', display: 'flex'}}>
                                                            <p style={{margin: '0 0 4px 0', fontWeight: 'bold', color: '#2c3e50'}}>
                                                                {activity.description}
                                                            </p>
                                                            <p style={{margin: 0, fontSize: '0.85rem', color: '#7f8c8d'}}>
                                                                {" (" + new Date(activity.date).toLocaleDateString()} by {activity.person + ")"}
                                                            </p>
                                                        </div>
                                                    ))}
                                                    {bug.activities.filter(activity => activity.person.toLowerCase() !== 'system').length > 2 && (
                                                        <p style={{margin: '8px 0 0 0', fontSize: '0.85rem', color: '#3498db', fontStyle: 'italic'}}>
                                                            +{bug.activities.filter(activity => activity.person.toLowerCase() !== 'system').length - 2} more...
                                                        </p>
                                                    )}
                                                </>
                                            ) : (
                                                <span style={{color: '#95a5a6', fontSize: '1.1rem'}}>Click to add activities</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {bugs.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                                    No bugs reported yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Activities Modal */}
            {showActivitiesModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000
                }}
                onClick={() => setShowActivitiesModal(null)}
                >
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '10px',
                        maxWidth: '750px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                            <h2 style={{margin: 0, fontSize: '1.5rem', color: '#2c3e50'}}>Activities</h2>
                            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.95rem'}}>
                                    <input
                                        type="checkbox"
                                        checked={showSystemActivities}
                                        onChange={(e) => setShowSystemActivities(e.target.checked)}
                                        style={{cursor: 'pointer', width: '16px', height: '16px'}}
                                    />
                                    <span>Show System Activities</span>
                                </label>
                                <button
                                    onClick={() => setShowActivitiesModal(null)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        color: 'red',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseOver={(e) => e.target.style.color = '#333'}
                                    onMouseOut={(e) => e.target.style.color = '#999'}
                                >✕</button>
                            </div>
                        </div>
                        
                        {/* Activities List */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            marginBottom: '20px',
                            paddingRight: '8px'
                        }}>
                            {bugs.find(b => b._id === showActivitiesModal)?.activities?.length > 0 ? (
                                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                                    {bugs.find(b => b._id === showActivitiesModal)?.activities?.filter(activity => {
                                        if (showSystemActivities) {
                                            return true;
                                        }
                                        return activity.person.toLowerCase() !== 'system';
                                    }).map((activity, idx) => (
                                        <div key={idx} style={{
                                            padding: '8px 4px',
                                            borderLeft: activity.person.toLowerCase() === 'system' ? '4px solid #95a5a6' : '4px solid #3498db',
                                            backgroundColor: activity.person.toLowerCase() === 'system' ? '#f5f5f5' : '#f9f9f9',
                                            borderRadius: '4px'
                                        }}>
                                            <p style={{margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 'bold', color: activity.person.toLowerCase() === 'system' ? '#7f8c8d' : '#2c3e50'}}>
                                                {activity.description}
                                            </p>
                                            <p style={{margin: 0, fontSize: '0.9rem', color: '#7f8c8d'}}>
                                                <strong>Date:</strong> {getDateFullFormat(activity.date)} by <strong>{activity.person}</strong>
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{
                                    padding: '10px',
                                    textAlign: 'center',
                                    color: '#95a5a6',
                                    fontStyle: 'italic'
                                }}>
                                    No activities yet. Add one below!
                                </div>
                            )}
                        </div>
                        
                        {/* Add New Activity Form */}
                        <div style={{
                            borderTop: '2px solid #ecf0f1',
                            paddingTop: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            {/* <h3 style={{margin: '0 0 12px 0', fontSize: '1rem', color: '#2c3e50', fontWeight: 'bold'}}>Add Activity</h3> */}
                            
                            <div className='d-flex'>
                                <textarea
                                    value={newActivityDescription}
                                    onChange={(e) => setNewActivityDescription(e.target.value)}
                                    placeholder="Enter activity description..."
                                    style={{
                                        width: '85%',
                                        borderRadius: '6px',
                                        border: '1.5px solid #bdc3c7',
                                        fontFamily: 'inherit',
                                        fontSize: '0.95rem',
                                        resize: 'vertical',
                                        minHeight: '40px',
                                        boxSizing: 'border-box',
                                        transition: 'all 0.2s',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#3498db';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(52, 152, 219, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#bdc3c7';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />

                                <div style={{position: 'relative', width: '100px'}}>
                                    <input
                                        type="text"
                                        value={personSearchInput}
                                        onChange={(e) => {
                                            setPersonSearchInput(e.target.value);
                                            setNewActivityPerson(e.target.value);
                                            setShowPersonDropdown(true);
                                        }}
                                        onFocus={() => setShowPersonDropdown(true)}
                                        placeholder="Select person..."
                                        style={{
                                            width: '100%',
                                            borderRadius: '6px',
                                            border: newActivityPerson.toLowerCase() === 'system' ? '2px solid #e74c3c' : '1.5px solid #bdc3c7',
                                            fontFamily: 'inherit',
                                            fontSize: '0.95rem',
                                            boxSizing: 'border-box',
                                            transition: 'all 0.2s',
                                            outline: 'none',
                                            padding: '8px',
                                            backgroundColor: newActivityPerson.toLowerCase() === 'system' ? '#fadbd8' : 'white'
                                        }}
                                        onBlur={() => setTimeout(() => setShowPersonDropdown(false), 200)}
                                    />
                                    {showPersonDropdown && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: 'white',
                                            border: '1.5px solid #3498db',
                                            borderTop: 'none',
                                            borderRadius: '0 0 6px 6px',
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            zIndex: 1000,
                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            {/* {personSearchInput.trim() && !uniquePeople.includes(personSearchInput.trim()) && (
                                                <div
                                                    onClick={() => {
                                                        setNewActivityPerson(personSearchInput.trim());
                                                        setPersonSearchInput('');
                                                        setShowPersonDropdown(false);
                                                    }}
                                                    style={{
                                                        padding: '10px 12px',
                                                        backgroundColor: '#e8f4f8',
                                                        borderBottom: '1px solid #ecf0f1',
                                                        cursor: 'pointer',
                                                        fontWeight: '600',
                                                        color: '#3498db',
                                                        fontSize: '0.95rem'
                                                    }}
                                                    onMouseOver={(e) => e.target.style.backgroundColor = '#d4e9f2'}
                                                    onMouseOut={(e) => e.target.style.backgroundColor = '#e8f4f8'}
                                                >
                                                    + Add "{personSearchInput.trim()}"
                                                </div>
                                            )} */}
                                            {filteredPeople.length > 0 ? (
                                                filteredPeople.map((person, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => {
                                                            // setNewActivityPerson(person);
                                                            setNewActivityPerson(person);
                                                            setPersonSearchInput(person);
                                                            setShowPersonDropdown(false);
                                                        }}
                                                        style={{
                                                            padding: '10px 12px',
                                                            borderBottom: idx < filteredPeople.length - 1 ? '1px solid #ecf0f1' : 'none',
                                                            cursor: 'pointer',
                                                            backgroundColor: newActivityPerson === person ? '#cce5ff' : 'white',
                                                            transition: 'background-color 0.2s',
                                                            fontSize: '0.95rem'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            if (newActivityPerson !== person) {
                                                                e.target.style.backgroundColor = '#f0f8ff';
                                                            }
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.target.style.backgroundColor = newActivityPerson === person ? '#cce5ff' : 'white';
                                                        }}
                                                    >
                                                        {person}
                                                    </div>
                                                ))
                                            ) : (
                                                <div style={{
                                                    padding: '10px 12px',
                                                    textAlign: 'center',
                                                    color: '#95a5a6',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    No matches
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {newActivityPerson.toLowerCase() === 'system' && (
                                <div style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#fadbd8',
                                    border: '1px solid #e74c3c',
                                    borderRadius: '4px',
                                    color: '#c0392b',
                                    fontSize: '0.9rem',
                                    fontWeight: '500'
                                }}>
                                    ⚠️ "system" is a reserved name and cannot be used for manual activities.
                                </div>
                            )}

                            <button
                                  onClick={handleAddActivity}
                                  disabled={newActivityPerson.toLowerCase() === 'system'}
                                  style={{
                                    width: '120px',
                                    padding: '10px',
                                    backgroundColor: newActivityPerson.toLowerCase() === 'system' ? '#bdc3c7' : '#3498db',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: newActivityDescription.trim() && newActivityPerson.trim() && newActivityPerson.toLowerCase() !== 'system' ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.2s',
                                    opacity: newActivityDescription.trim() && newActivityPerson.trim() && newActivityPerson.toLowerCase() !== 'system' ? 1 : 0.6,
                                    boxShadow: newActivityPerson.toLowerCase() === 'system' ? 'none' : '0 2px 8px rgba(52, 152, 219, 0.2)'
                                }}
                                onMouseOver={(e) => {
                                    if (newActivityDescription.trim() && newActivityPerson.trim() && newActivityPerson.toLowerCase() !== 'system') {
                                        e.target.style.backgroundColor = '#2980b9';
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.3)';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.backgroundColor = newActivityPerson.toLowerCase() === 'system' ? '#bdc3c7' : '#3498db';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = newActivityPerson.toLowerCase() === 'system' ? 'none' : '0 2px 8px rgba(52, 152, 219, 0.2)';
                                }}
                            >📤 Send</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
